import { numerosLoteDeTexto } from '../components/corte/familiasProdutos';
import { chaveLotePersistencia } from './tempoProducaoPecaLote';
import {
  lerRegistrosTempoProducaoPeca,
  salvarRegistrosTempoProducaoPeca,
} from './tempoProducaoPecaPersistencia';

export function normalizarLoteTexto(s: string): string {
  return String(s || '').trim().toUpperCase();
}

/** Lote do cadastro coincide exatamente com o alvo (ex.: 8798). */
export function loteCadastroCoincide(lote: string, alvo: string): boolean {
  return normalizarLoteTexto(lote) === normalizarLoteTexto(alvo);
}

/** Substitui o lote antigo pelo novo em textos compostos (ex.: 8798-8800 → 9000-8800). */
export function substituirNumeroLoteEmTexto(texto: string, loteAntigo: string, loteNovo: string): string {
  const antigo = normalizarLoteTexto(loteAntigo);
  const novo = normalizarLoteTexto(loteNovo);
  const atual = String(texto || '').trim();
  if (!antigo || !novo) return atual;
  if (normalizarLoteTexto(atual) === antigo) return novo;

  const numsAntigo = numerosLoteDeTexto(antigo);
  const numsNovo = numerosLoteDeTexto(novo);
  if (numsAntigo.length !== 1 || numsNovo.length !== 1) {
    return atual;
  }
  const de = numsAntigo[0];
  const para = numsNovo[0];
  const re = new RegExp(`(^|[^\\d])${de}(?=[^\\d]|$)`, 'g');
  return atual.replace(re, (_m, prefix: string) => `${prefix}${para}`);
}

export interface LoteProdutoRenumeravel {
  id: string;
  lote: string;
  produtoNome: string;
  qtd: number;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
  corProduto?: string;
  codigoProduto?: string;
}

export interface CardTempoCorteRenumeravel {
  id?: string;
  lote?: string;
  produtosOrigemCompleto?: Array<{ lote?: string; [k: string]: unknown }>;
  produtosOrigem?: Array<{ lote?: string; [k: string]: unknown }>;
}

export interface OpcoesRenumerarLoteCompleto {
  loteAntigo: string;
  loteNovo: string;
  atualizarDatas?: boolean;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
}

export interface ResultadoRenumerarLoteCompleto {
  lotesProdutos: number;
  cardsTempoCorte: number;
  relatorioItens: number;
  historicoLinhas: number;
  marcasFinalizado: number;
  marcasPalete: number;
}

export function renumerarLotesProdutos(
  lista: LoteProdutoRenumeravel[],
  opts: OpcoesRenumerarLoteCompleto
): { lista: LoteProdutoRenumeravel[]; alterados: number } {
  const antigo = normalizarLoteTexto(opts.loteAntigo);
  const novo = normalizarLoteTexto(opts.loteNovo);
  if (!antigo || !novo || antigo === novo) return { lista, alterados: 0 };

  let alterados = 0;
  const next = lista.map((lp) => {
    if (!loteCadastroCoincide(lp.lote, antigo)) return lp;
    alterados += 1;
    return {
      ...lp,
      lote: novo,
      ...(opts.atualizarDatas
        ? {
            dataHoraInicioCorte: opts.dataHoraInicioCorte || lp.dataHoraInicioCorte,
            dataHoraFimEmbalagem: opts.dataHoraFimEmbalagem || lp.dataHoraFimEmbalagem,
          }
        : {}),
    };
  });
  return { lista: next, alterados };
}

export function renumerarCardsTempoCorte<T extends CardTempoCorteRenumeravel>(
  cards: T[],
  loteAntigo: string,
  loteNovo: string
): { lista: T[]; alterados: number } {
  const antigo = normalizarLoteTexto(loteAntigo);
  const novo = normalizarLoteTexto(loteNovo);
  if (!antigo || !novo || antigo === novo) return { lista: cards, alterados: 0 };

  let alterados = 0;
  const next = cards.map((card) => {
    let mudou = false;
    let loteCard = card.lote;
    if (loteCard) {
      const novoLote = substituirNumeroLoteEmTexto(loteCard, antigo, novo);
      if (novoLote !== loteCard) {
        loteCard = novoLote;
        mudou = true;
      }
    }

    const mapOrigens = <O extends { lote?: string }>(arr: O[] | undefined): O[] | undefined => {
      if (!arr?.length) return arr;
      let arrMudou = false;
      const mapped = arr.map((o) => {
        if (!o.lote) return o;
        const nl = substituirNumeroLoteEmTexto(o.lote, antigo, novo);
        if (nl === o.lote) return o;
        arrMudou = true;
        return { ...o, lote: nl };
      });
      return arrMudou ? mapped : arr;
    };

    const poc = mapOrigens(card.produtosOrigemCompleto);
    const po = mapOrigens(card.produtosOrigem);
    if (poc !== card.produtosOrigemCompleto || po !== card.produtosOrigem) mudou = true;

    if (!mudou) return card;
    alterados += 1;
    return {
      ...card,
      lote: loteCard,
      produtosOrigemCompleto: poc,
      produtosOrigem: po,
    };
  });

  return { lista: next, alterados };
}

export function renumerarListaTextoLote(
  lista: string[],
  loteAntigo: string,
  loteNovo: string
): { lista: string[]; alterados: number } {
  const antigo = normalizarLoteTexto(loteAntigo);
  const novo = normalizarLoteTexto(loteNovo);
  if (!antigo || !novo || antigo === novo) return { lista, alterados: 0 };

  let alterados = 0;
  const set = new Set<string>();
  for (const item of lista) {
    const norm = normalizarLoteTexto(item);
    if (norm === antigo) {
      set.add(novo);
      alterados += 1;
    } else {
      set.add(item);
    }
  }
  return { lista: [...set], alterados };
}

export async function renumerarPersistenciaTempoProducaoPeca(
  loteAntigo: string,
  loteNovo: string
): Promise<boolean> {
  const antigo = normalizarLoteTexto(loteAntigo);
  const novo = normalizarLoteTexto(loteNovo);
  if (!antigo || !novo || antigo === novo) return false;

  const chaveAntiga = chaveLotePersistencia(antigo);
  const chaveNova = chaveLotePersistencia(novo);
  if (chaveAntiga === chaveNova) return false;

  const mapa = await lerRegistrosTempoProducaoPeca();
  const reg = mapa[chaveAntiga];
  if (!reg) return false;

  delete mapa[chaveAntiga];
  mapa[chaveNova] = {
    ...reg,
    loteChave: chaveNova,
    loteDigitado: novo,
  };
  await salvarRegistrosTempoProducaoPeca(mapa);
  return true;
}
